// Utilidades compartidas de las pantallas de POS (CRUD directo a PostgREST vía el
// atajo /rest/pos.<tabla>). Mismo patrón que features/inventario/shared.tsx.
import { ReactNode, useEffect, useMemo } from 'react';
import i18n from '@/lib/i18n';
import { useQuery } from '@tanstack/react-query';
import { Construction } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import { uuid } from '@/lib/uuid';
import { configurarImpresion, type ImpresionConfig } from './print';
// offline.ts sólo importa TIPOS de aquí (`import type`), así que este import de
// valores no crea ciclo en tiempo de ejecución.
import { catalogoOffline } from './offline';
import { PanelIntegrado } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const base = (t: string) => `/rest/pos.${t}`;
const repr = { headers: { Prefer: 'return=representation' } };

export const pos = {
  list: async <T,>(t: string, query = ''): Promise<T[]> => (await apiClient.get<T[]>(`${base(t)}${query}`)).data,
  create: async (t: string, body: unknown) => (await apiClient.post(base(t), body, repr)).data,
  update: async (t: string, id: string, body: unknown) => (await apiClient.patch(`${base(t)}?id=eq.${id}`, body, repr)).data,
  remove: async (t: string, id: string) => (await apiClient.delete(`${base(t)}?id=eq.${id}`)).data,
};

export const fieldCls =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function Campo({ label, children, className = '' }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={`block space-y-1 ${className}`}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <label className={`flex items-center gap-2 text-sm select-none ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
      <input type="checkbox" checked={checked} disabled={disabled}
             onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-input" />
      {label}
    </label>
  );
}

// Regla del proyecto (2026-10-10): sin ventanas modales. «Modal» se conserva por
// compatibilidad, pero muestra un panel integrado en la página.
export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return <PanelIntegrado title={title} onClose={onClose}>{children}</PanelIntegrado>;
}

// Pantalla placeholder para las opciones aún no implementadas (fase futura).
export function EnConstruccion({ titulo }: { titulo: string }) {
  return (
    <div className="p-6">
      <h1 className="text-xl font-semibold mb-4">{titulo}</h1>
      <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed py-16 text-center text-muted-foreground">
        <Construction className="w-10 h-10" />
        <p className="text-sm">{i18n.t('shell.coming_soon')}</p>
      </div>
    </div>
  );
}

export const TIPO_FORMA_PAGO = ['efectivo', 'tarjeta', 'transferencia', 'cheque', 'credito', 'otro'];
export const TIPO_DENOMINACION = ['billete', 'moneda'];

// Código SRI (tabla 24 - Formas de pago) para la factura electrónica.
export const CODIGOS_SRI_PAGO: { value: string; label: string }[] = [
  { value: '01', label: '01 · Sin utilización del sistema financiero (efectivo)' },
  { value: '15', label: '15 · Compensación de deudas' },
  { value: '16', label: '16 · Tarjeta de débito' },
  { value: '17', label: '17 · Dinero electrónico' },
  { value: '18', label: '18 · Tarjeta prepago' },
  { value: '19', label: '19 · Tarjeta de crédito' },
  { value: '20', label: '20 · Otros con utilización del sistema financiero (transferencias, DeUna…)' },
  { value: '21', label: '21 · Endoso de títulos' },
];
// Sugerencia por tipo (el usuario puede cambiarla)
export const codigoSriSugerido = (tipo: string): string => {
  switch (tipo) {
    case 'efectivo': return '01';
    case 'tarjeta': return '19';
    case 'credito': return '19';
    case 'transferencia': return '20';
    case 'cheque': return '20';
    default: return '01';
  }
};

export interface Caja {
  id: string; codigo: string | null; nombre: string; location_id: string | null; centro_costo_id: string | null; cc_contable_id: string | null;
  establecimiento: string | null; punto_emision: string | null; ambiente: string | null; es_activo: boolean;
  modo_impresion?: 'terminal' | 'red'; impresora_host?: string | null; impresora_puerto?: number | null;
  permite_nota_venta?: boolean;
}
export interface PartnerOpt { id: string; razon_social: string; tipo_partner: string[] }
export const centrosCostoList = async (): Promise<PartnerOpt[]> => {
  const all = (await apiClient.post<PartnerOpt[]>('/rpc/sri_partners', {}, { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } })).data || [];
  return all.filter((p) => (p.tipo_partner || []).includes('centro_costo'));
};
export interface FormaPago { id: string; codigo: string | null; nombre: string; tipo: string; requiere_referencia: boolean; orden: number; es_activo: boolean; cuenta_contable_id: string | null; codigo_sri: string | null; comision_pct: number | null }
export interface Denominacion { id: string; valor: number; tipo: string; orden: number; es_activo: boolean }
export interface LocationOpt { id: string; codigo: string | null; nombre: string }

// Selector de LOCAL de las pantallas del restaurante: con un solo local no hay
// nada que elegir (se oculta); con varios, muestra el combo rotulado "Local".
export function SelectorLocal({ locationId, setLocationId, locations }:
  { locationId: string; setLocationId: (v: string) => void; locations: LocationOpt[] }) {
  if (locations.length <= 1) return null;
  return (
    <label className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">Local</span>
      <select className={`${fieldCls} !w-auto`} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
        {locations.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
      </select>
    </label>
  );
}

export const locationsList = async (): Promise<LocationOpt[]> =>
  (await apiClient.get<LocationOpt[]>('/rest/inventario.locations?select=id,codigo,nombre&es_activo=eq.true&order=nombre')).data;

// Plan de cuentas (contabilidad) para enlazar formas de pago a su cuenta de control.
export interface CuentaContableOpt { id: string; codigo: string; nombre: string; es_movimiento: boolean }
export const cuentasContables = async (): Promise<CuentaContableOpt[]> =>
  (await apiClient.post<CuentaContableOpt[]>('/rpc/cont_plan', {}, { headers: { 'Content-Profile': 'contabilidad', 'Accept-Profile': 'contabilidad' } })).data || [];

// Centros de costo CONTABLES (contabilidad.centros_costo) para enlazar la caja.
export interface CentroContableOpt { id: string; codigo: string | null; nombre: string }
export const centrosContablesList = async (): Promise<CentroContableOpt[]> =>
  (await apiClient.post<CentroContableOpt[]>('/rpc/cont_centros', {}, { headers: { 'Content-Profile': 'contabilidad', 'Accept-Profile': 'contabilidad' } })).data || [];

// --- Punto de venta -------------------------------------------------------
export const POS = { headers: { 'Content-Profile': 'pos', 'Accept-Profile': 'pos' } };
export const posRpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body, POS)).data;

// Carga la configuración de impresión (formato del ticket) y la aplica al módulo
// `print.ts` (CFG a nivel de módulo) vía efecto — sin render propio. Llamarlo en
// cualquier pantalla que imprima (venta, ventas del día, salón, devoluciones)
// para que imprimirTicket/imprimirNotaCredito respeten los toggles configurados.
export function useImpresionConfig() {
  const { data } = useQuery<ImpresionConfig>({
    queryKey: ['pos-impresion-config'],
    queryFn: () => posRpc<ImpresionConfig>('pos_impresion_config_get'),
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => { if (data) configurarImpresion(data); }, [data]);
}

// Impresión de ticket por impresora de RED (ESC/POS vía print-agent): encola el
// ticket en la caja indicada; devuelve encolado:false + motivo si la caja no está
// en modo 'red' o no tiene host configurado.
export const posTicketImprimir = (cajaId: string, payload: unknown) =>
  posRpc<{ encolado: boolean; motivo?: string }>('pos_ticket_imprimir', { _caja: cajaId, _payload: payload });

// Decimales y locale por moneda (COP no usa decimales; USD/PEN sí). El default es
// USD/es-EC, así que `money(v)` sin moneda se comporta como antes en todo el ERP.
export { fechaLocal } from '@/lib/utils/fecha';

export const decimalesMoneda = (moneda?: string | null): number => (moneda === 'COP' ? 0 : 2);
const localeMoneda = (moneda?: string | null): string =>
  moneda === 'COP' ? 'es-CO' : moneda === 'PEN' ? 'es-PE' : LOCALE;
// Redondeo en el cliente espejo de financiero.redondear_moneda (el backend es la fuente
// de verdad): para que el preview de total/vuelto coincida con lo que registra la venta.
export const redondearMoneda = (v: number, moneda?: string | null): number => {
  const f = 10 ** decimalesMoneda(moneda);
  // toPrecision(12) corrige el error binario de los medios centavos exactos
  // (168.425 en float es 168.42499999… → Math.round daría 168.42; numeric da 168.43)
  return Math.round(Number((v * f).toPrecision(12))) / f;
};
export const money = (v: number | string | null, moneda?: string | null) => {
  const dec = decimalesMoneda(moneda);
  return Number(v ?? 0).toLocaleString(localeMoneda(moneda), {
    style: 'currency', currency: moneda || 'USD', minimumFractionDigits: dec, maximumFractionDigits: dec,
  });
};

export interface ProductoVenta { id: string; codigo: string; nombre: string; iva_tarifa: number; precio_base: number; controla_stock: boolean; maneja_lote: boolean; unidad_medida: string; fracciones: number; marca?: string | null }

// Resaltado reutilizable para la fila activa en cualquier lista del POS que se
// navegue con teclado/mouse (carrito, buscador de productos F1, clientes F5, …).
// La fila activa se resalta (borde + fondo) y hace un leve "zoom" (scale) de su
// información; al pasar el mouse por otras filas se agrandan un poco también.
export const navRowCls = (active: boolean) =>
  `relative origin-left transition-transform duration-150 ${
    active
      ? 'z-10 bg-primary/15 ring-2 ring-inset ring-primary scale-[1.02] shadow-sm'
      : 'hover:z-10 hover:bg-muted/40 hover:scale-[1.01]'
  }`;
export interface ClienteOpt { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[] }

const PROD_COLS = 'id,codigo,nombre,iva_tarifa,precio_base,controla_stock,maneja_lote,unidad_medida,fracciones';
const INVP = { headers: { 'Content-Profile': 'inventario', 'Accept-Profile': 'inventario' } };

interface ProductoRowMarca { id: string; codigo: string; nombre: string; iva_tarifa: number; precio_base: number; controla_stock: boolean; maneja_lote: boolean; unidad_medida: string; fracciones: number; marca: { nombre: string } | null }
export const buscarProductoPorCodigo = async (code: string): Promise<ProductoVenta[]> => {
  const c = encodeURIComponent(code.trim());
  const rows = (await apiClient.get<ProductoRowMarca[]>(
    `/rest/inventario.productos?select=${PROD_COLS},marca:marca_id(nombre)&es_activo=eq.true&tiene_variantes=eq.false&or=(codigo.eq.${c},codigo_barras1.eq.${c},codigo_barras2.eq.${c},codigo_barras3.eq.${c})&limit=1`)).data;
  return rows.map((r) => ({ ...r, marca: r.marca?.nombre ?? null }));
};
export const buscarProductosNombre = async (q: string): Promise<ProductoVenta[]> =>
  (await apiClient.get<ProductoVenta[]>(
    `/rest/inventario.productos?select=${PROD_COLS}&es_activo=eq.true&es_vendible=eq.true&tiene_variantes=eq.false&nombre=ilike.*${encodeURIComponent(q)}*&order=nombre&limit=30`)).data;

// Búsqueda enriquecida para el POS: en 1 round trip trae stock en la bodega +
// % de descuento vigente (RPC inventario.pos_buscar_productos), sin N+1.
// solo_efectivo/participa_debito/participa_credito son opcionales: `pos_buscar_productos`
// todavía no los expone (columnas listas en inventario.productos, script 626) — quedan
// listos para cuando el RPC los devuelva; ausentes = comportamiento actual (no restringe).
export interface ProductoBusqueda extends ProductoVenta { principio_activo: string | null; presentacion: string | null; fracciones: number; marca_id: string | null; marca: string | null; stock: number; stock_min: number; stock_max: number; descuento_pct: number; descuento_origen: string; clase_local: string | null; clase_empresa: string | null; es_lento: boolean; transito_entrante?: number; transito_eta?: string | null; solo_efectivo?: boolean; participa_debito?: boolean; participa_credito?: boolean }
export const buscarProductosPos = async (q: string, location: string | null, partner: string | null): Promise<ProductoBusqueda[]> =>
  (await apiClient.post<ProductoBusqueda[]>('/rpc/pos_buscar_productos', { p_q: q, p_location: location || null, p_partner: partner || null }, INVP)).data;
// F8: todos los productos de una marca
export const buscarProductosPorMarca = async (marca: string, location: string | null, partner: string | null): Promise<ProductoBusqueda[]> =>
  (await apiClient.post<ProductoBusqueda[]>('/rpc/pos_buscar_productos', { p_q: '', p_location: location || null, p_partner: partner || null, p_marca: marca }, INVP)).data;
// F9: equivalentes = productos con el mismo principio activo
export const buscarProductosPorPrincipio = async (pa: string, location: string | null, partner: string | null): Promise<ProductoBusqueda[]> =>
  (await apiClient.post<ProductoBusqueda[]>('/rpc/pos_buscar_productos', { p_q: '', p_location: location || null, p_partner: partner || null, p_pa: pa }, INVP)).data;
// Botones de familia: todos los productos de una categoría
export const buscarProductosPorCategoria = async (categoria: string, location: string | null, partner: string | null): Promise<ProductoBusqueda[]> =>
  (await apiClient.post<ProductoBusqueda[]>('/rpc/pos_buscar_productos', { p_q: '', p_location: location || null, p_partner: partner || null, p_categoria: categoria }, INVP)).data;

export interface CategoriaVenta { id: string; codigo: string | null; nombre: string; orden: number }
export const categoriasVenta = async (): Promise<CategoriaVenta[]> =>
  (await apiClient.post<CategoriaVenta[]>('/rpc/pos_categorias_venta', {}, INVP)).data;

// F4: stock del producto en cada bodega (cajas + fracciones)
export interface StockSucursal { location_id: string; codigo: string | null; nombre: string; tipo: string; cajas: number; fracciones: number }
export const stockPorSucursal = async (productoId: string): Promise<StockSucursal[]> =>
  (await apiClient.post<StockSucursal[]>('/rpc/stock_por_sucursal', { p_producto: productoId }, INVP)).data;

// F5: kardex del producto por rango de fechas (default 1er día del mes → hoy)
export interface KardexMov { fecha: string; bodega: string; tipo: string; documento: string | null; entrada: number; salida: number; saldo: number; costo_unitario: number; referencia: string | null; observacion: string | null; contraparte: string | null }
export const kardexProducto = async (productoId: string, desde: string, hasta: string): Promise<KardexMov[]> =>
  (await apiClient.post<KardexMov[]>('/rpc/kardex_producto', { p_producto: productoId, p_desde: desde, p_hasta: hasta }, INVP)).data;

// F2: historial de compras del cliente (líneas de sus últimas 100 ventas).
export interface HistorialCompra { fecha: string; tipo_documento: string; documento: string | null; producto: string; cantidad: number; descuento: number; precio_neto: number }
export const historialCliente = (clienteId: string): Promise<HistorialCompra[]> =>
  posRpc<HistorialCompra[]>('pos_historial_cliente', { _cliente: clienteId });

// Stock de varios productos en una bodega, para la columna Stock del carrito.
//
// Devuelve `ok` aparte del mapa, y esa distinción es el punto: un producto que
// NO está en el mapa con ok=true tiene cero de verdad; con ok=false no sabemos
// nada. Pintar un cero rojo cuando la consulta falló es peor que no pintar
// nada: el cajero ve "sin stock" en TODO el carrito, no se atreve a cobrar, y
// la causa real (la consulta que no llegó) queda invisible.
//
// Va en LOTES de 80: un in.() con cientos de UUID supera el límite de línea de
// petición de nginx (414 medido a partir de ~230 ids) y entonces se pierde la
// respuesta ENTERA, no una parte. Es el mismo motivo por el que `productoThumbs`
// ya batchea.
const LOTE_STOCK = 80;
export const stockProductos = async (
  ids: string[], location: string | null,
): Promise<{ stock: Record<string, number>; ok: boolean }> => {
  if (!location || !ids.length) return { stock: {}, ok: true };
  const unicos = [...new Set(ids)];
  const stock: Record<string, number> = {};
  for (let i = 0; i < unicos.length; i += LOTE_STOCK) {
    const lote = unicos.slice(i, i + LOTE_STOCK);
    const rows = (await apiClient.get<{ producto_id: string; unidades: number }[]>(
      `/rest/inventario.stockcurrent?select=producto_id,unidades&location_id=eq.${location}&producto_id=in.(${lote.join(',')})`)).data;
    for (const r of rows) stock[r.producto_id] = Number(r.unidades);
  }
  return { stock, ok: true };
};

// La misma columna SIN red: el stock del último snapshot del catálogo. Es la
// fuente que ya usa la búsqueda cuando no hay conexión — por eso al cajero le
// "sale todo bien el stock" en F1 — así que la columna del carrito debe leer de
// ahí en vez de quedarse en cero y contradecir a la pantalla de al lado.
export const stockProductosOffline = async (
  ids: string[], location: string | null,
): Promise<{ stock: Record<string, number>; ok: boolean }> => {
  if (!location || !ids.length) return { stock: {}, ok: true };
  const cat = await catalogoOffline(location);
  const porId = new Map(cat.map((p) => [p.id, Number(p.stock) || 0]));
  const stock: Record<string, number> = {};
  let completo = true;
  for (const id of new Set(ids)) {
    if (porId.has(id)) stock[id] = porId.get(id) as number;
    else completo = false;   // no estaba en el snapshot: no lo sabemos
  }
  return { stock, ok: completo };
};

// Datos de la bodega de la caja: nombre (sucursal) + unidad de venta ('caja'|'fraccion').
export const bodegaInfo = async (location: string | null): Promise<{ nombre: string; unidad_venta: string }> => {
  if (!location) return { nombre: '', unidad_venta: 'caja' };
  const rows = (await apiClient.get<{ nombre: string; unidad_venta: string }[]>(
    `/rest/inventario.locations?select=nombre,unidad_venta&id=eq.${location}`)).data;
  return { nombre: rows[0]?.nombre ?? '', unidad_venta: rows[0]?.unidad_venta ?? 'caja' };
};

// Stock actual de un producto en una bodega (índice único stockcurrent).
export const stockProducto = async (productoId: string, location: string | null): Promise<number> => {
  if (!location) return 0;
  const rows = (await apiClient.get<{ unidades: number }[]>(
    `/rest/inventario.stockcurrent?select=unidades&location_id=eq.${location}&producto_id=eq.${productoId}`)).data;
  return Number(rows[0]?.unidades ?? 0);
};

// Thumbnails de los productos en una sola consulta batch (tabla satélite,
// indexada por PK). Sólo para los resultados visibles → no toca la búsqueda.
export const productoThumbs = async (ids: string[]): Promise<Record<string, string>> => {
  const unicos = [...new Set(ids)];
  if (!unicos.length) return {};
  // En LOTES: un in.() con cientos de UUIDs supera el límite de URL de nginx
  // (con ~584 ids la petición se rechazaba y "Fotos del menú" salía sin fotos).
  const LOTE = 80;
  const lotes: string[][] = [];
  for (let i = 0; i < unicos.length; i += LOTE) lotes.push(unicos.slice(i, i + LOTE));
  const respuestas = await Promise.all(lotes.map(async (lote) =>
    (await apiClient.get<{ producto_id: string; thumb: string | null }[]>(
      `/rest/inventario.producto_imagenes?producto_id=in.(${lote.join(',')})&select=producto_id,thumb`)).data));
  const map: Record<string, string> = {};
  for (const rows of respuestas) for (const r of rows) if (r.thumb) map[r.producto_id] = r.thumb;
  return map;
};

// Imagen grande de UN producto (para la vista previa del buscador F1)
export const productoImagen = async (id: string): Promise<string | null> => {
  const rows = (await apiClient.get<{ imagen: string | null; thumb: string | null }[]>(
    `/rest/inventario.producto_imagenes?producto_id=eq.${id}&select=imagen,thumb`)).data;
  return rows[0]?.imagen ?? rows[0]?.thumb ?? null;
};

export const resolverPrecio = async (productoId: string, partnerId: string | null): Promise<{ precio: number; iva_tarifa: number }> =>
  (await apiClient.post('/rpc/resolver_precio', { _producto_id: productoId, _partner_id: partnerId || null, _sentido: 'venta' }, INVP)).data;

// % de descuento a aplicar (bodega/letra vs cliente; promo>volumen>base)
export const descuentoVenta = async (productoId: string, locationId: string | null, cantidad: number, partnerId: string | null): Promise<number> =>
  (await apiClient.post<number>('/rpc/descuento_venta', { p_producto: productoId, p_location: locationId || null, p_cantidad: cantidad, p_partner: partnerId || null }, INVP)).data;

export const fidelidadSaldo = async (partner: string): Promise<number> =>
  (await apiClient.post<number>('/rpc/fidelidad_saldo', { p_partner: partner }, POS)).data;
export const monederoSaldo = async (partner: string): Promise<number> =>
  (await apiClient.post<number>('/rpc/monedero_saldo', { p_partner: partner }, POS)).data;
export interface NivelFid { id: string; codigo: string | null; nombre: string; monto_min: number; multiplicador_puntos: number; descuento_pct: number; orden: number; es_activo: boolean }
export const fidelidadNivel = async (partner: string): Promise<{ nombre: string; multiplicador: number; descuento_pct: number; compra_12m: number } | null> =>
  (await apiClient.post('/rpc/fidelidad_nivel', { p_partner: partner }, POS)).data;
export interface FidelidadConfig { activo: boolean; puntos_por_dolar: number; valor_punto: number; canje_minimo: number; caducidad_meses: number; cashback_activo: boolean; cashback_pct: number; cashback_caducidad_meses: number }
export const fidelidadConfigGet = async (): Promise<FidelidadConfig | null> =>
  (await apiClient.post<FidelidadConfig | null>('/rpc/fidelidad_config_get', {}, POS)).data;
export const fidelidadConfigSet = async (c: FidelidadConfig): Promise<FidelidadConfig> =>
  (await apiClient.post<FidelidadConfig>('/rpc/fidelidad_config_set', { payload: c }, POS)).data;

export interface PromoEval { extra: Record<string, number>; global_pct: number; regalos: { producto_id: string; cantidad: number }[]; aplicadas: { nombre: string; tipo: string }[] }
export const promosEvaluar = async (cliente: string | null, lineas: { producto_id: string; cantidad: number; precio_unitario: number; descuento: number }[], location: string | null): Promise<PromoEval> =>
  (await apiClient.post<PromoEval>('/rpc/promos_evaluar', { p_cliente: cliente || null, p_lineas: lineas, p_location: location || null }, POS)).data;

export const clientesList = async (): Promise<ClienteOpt[]> => {
  const all = (await apiClient.post<ClienteOpt[]>('/rpc/sri_partners', {}, { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } })).data || [];
  return all.filter((p) => (p.tipo_partner || []).includes('cliente'));
};
// Alta rápida de cliente desde el POS (F5 → Insert). Idempotente por identificación.
export const clienteCrear = async (data: {
  razon_social: string; identificacion: string; tipo_identificacion: string;
  direccion?: string; telefono?: string; email?: string;
}): Promise<{ id: string; razon_social: string; identificacion: string; existe: boolean }> =>
  (await apiClient.post('/rpc/pos_cliente_crear', { _payload: data },
    { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } })).data;

export const formasPagoActivas = (): Promise<FormaPago[]> => pos.list<FormaPago>('formas_pago', '?select=*&es_activo=eq.true&order=orden,nombre');
export const cajasActivas = (): Promise<Caja[]> => pos.list<Caja>('cajas', '?select=*&es_activo=eq.true&order=nombre');
// Solo las cajas que el usuario logueado puede operar (según pos.caja_usuarios / nivel).
export const cajasOperables = (): Promise<Caja[]> => posRpc<Caja[]>('pos_cajas_operables');

export interface ProductoMin { id: string; codigo: string; nombre: string }
export const productosMinList = async (): Promise<ProductoMin[]> =>
  (await apiClient.get<ProductoMin[]>('/rest/inventario.productos?select=id,codigo,nombre&es_activo=eq.true&tiene_variantes=eq.false&order=nombre')).data;
export interface CategoriaOpt { id: string; nombre: string }
export const categoriasList = async (): Promise<CategoriaOpt[]> =>
  (await apiClient.get<CategoriaOpt[]>('/rest/inventario.categorias?select=id,nombre&order=nombre')).data;

// Fidelización Fase 4: sellos
export const PREMIOS_SELLO = [
  { v: 'producto', label: 'Producto gratis' },
  { v: 'monedero', label: 'Saldo a monedero ($)' },
  { v: 'puntos', label: 'Puntos' },
  { v: 'descuento', label: 'Descuento (%)' },
];
export interface SelloCampana {
  id: string; codigo: string | null; nombre: string;
  modo: 'producto' | 'visita' | 'monto'; monto_por_sello: number | null; monto_minimo: number;
  producto_id: string | null; categoria_id: string | null;
  sellos_por_unidad: number; sellos_meta: number; caducidad_meses: number;
  premio_tipo: string; premio_producto_id: string | null; premio_cantidad: number;
  premio_monto: number; premio_puntos: number; premio_descuento_pct: number;
  premio_caducidad_meses: number; vigente_desde: string | null; vigente_hasta: string | null; es_activo: boolean;
}
export interface SelloSaldo { campana_id: string; nombre: string; saldo: number; meta: number; premio_tipo: string; falta: number }
export const sellosSaldo = async (partner: string): Promise<SelloSaldo[]> =>
  (await apiClient.post<SelloSaldo[]>('/rpc/sellos_saldo', { p_partner: partner }, POS)).data || [];
export interface SelloPremio { id: string; campana: string | null; premio_tipo: string; descripcion: string; caduca_at: string | null }
export const sellosPremiosDisponibles = async (partner: string): Promise<SelloPremio[]> =>
  (await apiClient.post<SelloPremio[]>('/rpc/sellos_premios_disponibles', { p_partner: partner }, POS)).data || [];

export interface Promocion {
  id: string; codigo: string | null; nombre: string; tipo: string;
  vigente_desde: string | null; vigente_hasta: string | null; prioridad: number; es_activo: boolean;
  producto_id: string | null; nxm_n: number | null; nxm_m: number | null;
  cantidad_min: number | null; regalo_producto_id: string | null; regalo_cantidad: number | null;
  monto_min: number | null; monto_pct: number | null; combo_precio: number | null;
  hora_inicio: string | null; hora_fin: string | null; dias_semana: number[] | null; location_id: string | null;
}
// 0=domingo … 6=sábado (misma convención que extract(dow) y signage.programacion)
export const DIAS_SEMANA_MIN = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
export interface PromocionItem { id?: string; promocion_id?: string; producto_id: string; cantidad: number }
export const TIPOS_PROMO = [
  { v: 'nxm', label: 'NxM (2x1, 3x2)' },
  { v: 'combo', label: 'Combo / paquete' },
  { v: 'monto', label: 'Por monto de compra' },
  { v: 'regalo', label: 'Producto de regalo' },
];

export interface UsuarioOpt { id: string; username: string; email: string | null }
export const usuariosList = async (): Promise<UsuarioOpt[]> =>
  (await apiClient.get<UsuarioOpt[]>('/rest/autenticacion.usuarios?select=id,username,email&order=username')).data;
export interface CajaUsuario { id: string; caja_id: string; usuario_id: string; es_activo: boolean }
export const cajaUsuariosList = (cajaId: string): Promise<CajaUsuario[]> =>
  pos.list<CajaUsuario>('caja_usuarios', `?select=*&caja_id=eq.${cajaId}`);

export const denominacionesActivas = (): Promise<Denominacion[]> =>
  pos.list<Denominacion>('denominaciones', '?select=*&es_activo=eq.true&order=tipo.desc,valor.desc');

// Resuelve la caja de este equipo (huella en localStorage)
export function useTerminalCaja() {
  const deviceId = useMemo(() => getDeviceId(), []);
  const q = useQuery({ queryKey: ['pos-device-caja', deviceId], queryFn: () => posRpc<Caja | null>('pos_caja_por_device', { p_device_id: deviceId }) });
  return { caja: q.data ?? null, loading: q.isLoading };
}

// Huella del equipo (no hay acceso a la MAC desde el navegador)
export const getDeviceId = (): string => {
  let d = localStorage.getItem('pos_device_id');
  if (!d) { d = uuid(); localStorage.setItem('pos_device_id', d); }
  return d;
};

// --- De Una (QR Banco Pichincha) ------------------------------------------
export interface DeunaConfig {
  proveedor: string; ambiente: string; merchant_id: string | null; caja_deuna: string | null;
  base_url: string | null; forma_pago_id: string | null; es_activo: boolean;
  api_key_configurada: boolean; api_secret_configurada: boolean;
}
export interface DeunaCobro {
  id: string; orden: string; estado: string; monto: number;
  qr: string | null; deep_link: string | null; referencia_pago: string | null;
  expira_en: string | null; error: string | null;
}
export const deunaConfigGet = async (): Promise<DeunaConfig | null> => posRpc('deuna_config_get');
export const deunaConfigSet = async (data: Record<string, unknown>): Promise<DeunaConfig> => posRpc('deuna_config_set', { _data: data });
export const deunaCrearCobro = async (monto: number, cajaId: string | null): Promise<DeunaCobro> =>
  posRpc('deuna_crear_cobro', { _monto: monto, _caja_id: cajaId });
export const deunaEstado = async (cobroId: string): Promise<DeunaCobro> => posRpc('deuna_estado', { _cobro_id: cobroId });
export const deunaAnular = async (cobroId: string): Promise<unknown> => posRpc('deuna_anular', { _cobro_id: cobroId });

// --- Pasarela de cobro con tarjeta (PagoMedios) · scripts 879-880 ----------
// Tres modos conviven y se eligen POR MÓDULO: datáfono (terminal físico, se
// concilia después), enlace (el cliente paga en la página del proveedor) y
// directo (tarjeta tecleada aquí — somete todo a PCI-DSS).
export type ModuloPos = 'pos' | 'restaurant' | 'hotel' | 'taller';

export interface PasarelaModos {
  /** 902 · De Una por la misma pasarela. Necesita su PROPIA forma de pago
   *  (código SRI 20), distinta de la de tarjeta (19). */
  deuna?: boolean; forma_pago_deuna_id?: string | null;
  modulo: ModuloPos; configurado: boolean;
  proveedor?: string; ambiente?: string;
  forma_pago_id?: string | null; expira_segundos?: number;
  datafono: boolean; enlace: boolean; directo: boolean;
  credenciales_ok?: boolean; mensaje: string | null;
}
export interface PasarelaCobro {
  id: string; orden: string; estado: string; monto: number;
  url: string | null; qr: string | null; expira_en: string | null;
  referencia_pago: string | null; autorizacion: string | null;
  marca: string | null; ultimos4: string | null; error: string | null;
  // Lo que necesita la conciliación bancaria (script 882).
  lote: string | null; mid: string | null; tid: string | null; proveedor?: string | null;
  /** 'tarjeta' | 'deuna' — con qué pagó de verdad el cliente (902). */
  medio?: string | null;
  /** Quien LIQUIDA (PACIFICARD…), distinto de `proveedor` (la pasarela). */
  adquirente?: string | null;
  vencido?: boolean;
}
export interface PasarelaConfig {
  modulo: ModuloPos; proveedor: string; ambiente: string;
  base_url: string | null; merchant_id: string | null;
  forma_pago_id: string | null; es_activo: boolean;
  modo_datafono: boolean; modo_enlace: boolean; modo_directo: boolean;
  modo_deuna?: boolean; forma_pago_deuna_id?: string | null;   // 902
  expira_segundos: number; token_configurado: boolean; api_key_configurada: boolean;
  directo_habilitado_at: string | null; directo_habilitado_por: string | null;
}

export const pasarelaModos = async (modulo: ModuloPos): Promise<PasarelaModos> =>
  posRpc('pasarela_modos', { _modulo: modulo });
export const pasarelaConfigGet = async (modulo?: ModuloPos): Promise<PasarelaConfig[]> =>
  posRpc('pasarela_config_get', { _modulo: modulo ?? null });
export const pasarelaConfigSet = async (data: Record<string, unknown>): Promise<{ message: string }> =>
  posRpc('pasarela_config_set', { _data: data });
export const pasarelaCrearCobro = async (
  monto: number, modulo: ModuloPos, cajaId: string | null, partnerId: string | null,
  medio: 'tarjeta' | 'deuna' = 'tarjeta',
): Promise<PasarelaCobro> =>
  posRpc('pasarela_crear_cobro', {
    _monto: monto, _modulo: modulo, _caja_id: cajaId, _partner_id: partnerId, _medio: medio });
export const pasarelaEstado = async (cobroId: string): Promise<PasarelaCobro> =>
  posRpc('pasarela_estado', { _cobro_id: cobroId });
/** Reversa un cobro autorizado que NO tiene venta detrás (895). Con venta, la
 *  BD lo rechaza: eso se devuelve con nota de crédito. Sólo nivel >= 4. */
export const pasarelaReversar = (cobroId: string, motivo: string): Promise<{ id: string; estado: string; solicitado?: boolean; ya_reversado?: boolean; mensaje?: string }> =>
  posRpc('pasarela_reversar', { _cobro_id: cobroId, _motivo: motivo });
export const pasarelaAnular = async (cobroId: string): Promise<unknown> =>
  posRpc('pasarela_anular', { _cobro_id: cobroId });

// Estados en los que el cobro sigue vivo y hay que seguir preguntando.
export const PASARELA_ACTIVO = ['pendiente', 'enlace_listo', 'qr_generado'];

// Formas de pago permitidas en un local (forma sin asignaciones = todos los locales)
export const formasPagoLocal = (locationId: string | null): Promise<FormaPago[]> =>
  posRpc('pos_formas_pago_local', { _location_id: locationId });
export interface FormaPagoLocal { id: string; forma_pago_id: string; location_id: string }

// --- Recargas Broadnet ------------------------------------------------------
export interface BnProducto {
  id: string; proveedor_codigo: string; proveedor_nombre: string;
  servicio_codigo: string; servicio_nombre: string;
  soporta_reversa: boolean; soporta_consulta: boolean; orden: number; es_activo: boolean;
}
export interface BnVentaResp {
  id: string; referencia: string; estado: string; codigo_respuesta: string | null;
  mensaje: string | null; autorizacion: string | null; nota_venta: string | null;
  proveedor: string; celular: string;
  // `monto` es SÓLO la recarga. Lo que pagó el cliente es `monto_cobrado`:
  // facturar con `monto` dejaría fuera la comisión de gestión (832).
  monto: number;
  gestion?: number; gestion_iva?: number; monto_cobrado?: number;
}
export interface BnTx {
  venta_id?: string | null; venta_codigo?: string | null;
  id: string; fecha: string; lote: string; referencia: string; caja: string; cajero: string | null;
  proveedor: string; servicio: string; celular: string;
  // `monto` es el del SERVICIO —lo que se liquida con Broadnet— y NO lo que
  // entró en caja: eso es `monto_cobrado` (838).
  monto: number;
  comision_broadnet?: number; gestion?: number; gestion_iva?: number;
  monto_cobrado?: number; ganancia?: number;
  estado: string; tipo?: string;
  codigo_respuesta: string | null; mensaje: string | null; autorizacion: string | null;
  nota_venta: string | null; intentos: number;
}
export interface BnConfig { id: string; modo: string; host: string | null; esquema_comision: string | null; es_activo: boolean }
// Solo RECARGAS (móviles/TV): los servicios básicos tienen su propia pestaña
// (ServiciosBasicos, que usa bn_productos_listar('servicio_basico')).
// Por la RPC y no por la tabla: así viene `facturacion_efectiva` con la
// herencia ya resuelta en la base, y la regla de quién factura vive en un solo
// sitio en vez de repetirse en el cliente.
export const bnProductosActivos = async (): Promise<BnProductoFull[]> =>
  (await bnProductosListar('recarga')).filter((p) => p.es_activo);
// Quién factura un producto Broadnet, con la herencia ya resuelta en la base.
export const bnFacturacionDe = (bpId: string): Promise<'comercio' | 'broadnet'> =>
  posRpc('bn_facturacion_de', { _bp_id: bpId });
// Producto de inventario con el que se factura la comisión de gestión.
export const bnGestionProductoAsegurar = async (): Promise<string> =>
  (await apiClient.post('/rpc/bn_gestion_producto_asegurar', {}, POS)).data;

export interface BnDesglose {
  tecleado: number; captura: 'total' | 'recarga'; recarga: number;
  comision: number; comision_base: number; comision_iva: number;
  comision_iva_pct: number; total: number;
}
// Desglose PREVIO: para que el cajero vea qué va a recargar y qué va a cobrar
// antes de pulsar. Con la comisión desactivada devuelve recarga = total.
export const bnGestionDesglosar = async (productoId: string, monto: number): Promise<BnDesglose> =>
  (await apiClient.post('/rpc/bn_gestion_desglosar',
    { _bp_id: productoId, _tecleado: monto }, POS)).data;

export const bnVender = (cajaId: string, productoId: string, celular: string, monto: number): Promise<BnVentaResp> =>
  posRpc('bn_vender', { _caja_id: cajaId, _producto_id: productoId, _celular: celular, _monto: monto });
export const bnRegularizar = (txId: string): Promise<{ id: string; estado: string; mensaje: string | null }> =>
  posRpc('bn_regularizar', { _tx_id: txId });
/** Descarta una recarga que demostrablemente NUNCA salió a Broadnet (889).
 *  Sólo supervisor, y sólo si no hay ni un rastro de respuesta del proveedor. */
/** Factura una recarga aprobada que se quedó sin comprobante (901). Exige turno
 *  abierto en su caja y nivel de supervisor. */
export const bnFacturarRecarga = (txId: string): Promise<{ codigo?: string; total?: string; ya_facturada?: boolean }> =>
  posRpc('bn_facturar_recarga', { _tx_id: txId });
export const bnDescartar = (txId: string, motivo: string): Promise<{ id: string; estado: string; descartada?: boolean; ya_resuelta?: boolean; mensaje?: string }> =>
  posRpc('bn_descartar', { _tx_id: txId, _motivo: motivo });
export const bnTransacciones = (desde: string | null, hasta: string | null, caja: string | null, estado: string | null): Promise<BnTx[]> =>
  posRpc('bn_transacciones', { _desde: desde, _hasta: hasta, _caja: caja, _estado: estado });
export const bnPendientesCaja = (cajaId: string): Promise<{ id: string; estado: string }[]> =>
  pos.list('broadnet_transacciones', `?select=id,estado&caja_id=eq.${cajaId}&estado=in.(pendiente,reverso_pendiente,confirmacion_pendiente)`);

// Trazabilidad: línea de tiempo de las llamadas del worker al WebService de Broadnet
// por transacción (bn_ws_log). En modo simulado no hay logs reales (el worker no llama).
export interface BnWsLogTx {
  id: string; referencia: string; estado: string; codigo_respuesta: string | null; mensaje: string | null;
  autorizacion: string | null; intentos: number; celular: string; proveedor: string; monto: number;
  creada: string; actualizada: string;
}
export interface BnWsLogEntry {
  creado_at: string; fase: string; operacion: string | null; tipo_transaccion: string | null; intento: number;
  http_status: number | null; duracion_ms: number | null; codigo_respuesta: string | null; mensaje: string | null;
  autorizacion: string | null; resultado: string | null; respuesta_xml: string | null; request_resumen: string | null;
  request_xml: string | null; // sobre SOAP enviado, con <clave> enmascarada (707)
}
// Consulta/pago asociados en la traza (702): los DOS XML del servicio juntos.
export interface BnTrazaConsultaAsociada {
  id: string; estado: string; deuda: number | null; comision: number | null;
  titular: string | null; creada: string; logs: BnWsLogEntry[];
}
export interface BnTrazaPagoAsociado {
  id: string; referencia: string; estado: string; codigo_respuesta: string | null;
  mensaje: string | null; autorizacion: string | null; monto: number; creada: string; logs: BnWsLogEntry[];
}
export interface BnWsLog { tx: BnWsLogTx; logs: BnWsLogEntry[]; consulta?: BnTrazaConsultaAsociada | null }
export const bnWsLog = (txId: string): Promise<BnWsLog> => posRpc<BnWsLog>('bn_ws_log', { _tx_id: txId });

// --- Trazabilidad de CONSULTAS DE DEUDA (Fase 4 + script 700) ---
export interface BnConsultaRow {
  id: string; fecha: string; proveedor: string; referencia_servicio: string; referencia: string;
  estado: string; deuda: number | null; comision: number | null; titular: string | null;
  codigo_respuesta: string | null; mensaje: string | null; intentos: number; caja: string | null;
}
export interface BnConsultaWsCab {
  id: string; referencia: string; referencia_servicio: string; estado: string;
  codigo_respuesta: string | null; mensaje: string | null;
  deuda: number | null; comision: number | null; titular: string | null;
  intentos: number; proveedor: string; creada: string; actualizada: string;
}
export interface BnConsultaWsLog { consulta: BnConsultaWsCab; logs: BnWsLogEntry[]; pago?: BnTrazaPagoAsociado | null }
export const bnConsultasListar = (desde: string | null, hasta: string | null): Promise<BnConsultaRow[]> =>
  posRpc<BnConsultaRow[]>('bn_consultas_listar', { _desde: desde, _hasta: hasta });
export const bnConsultaWsLog = (consultaId: string): Promise<BnConsultaWsLog> =>
  posRpc<BnConsultaWsLog>('bn_consulta_ws_log', { _consulta_id: consultaId });
// --- Código de PESO VARIABLE de balanza etiquetadora (EAN-13, estándar GS1
// in-store). Formato CONFIGURABLE por empresa (706, pantalla POS →
// Configuración → Balanza / Etiqueta de peso): prefijos, posición/longitud del
// PLU y del VALOR, tipo de valor (peso en gramos o PRECIO en centavos) y si se
// valida el dígito verificador. Default = estándar: 20|21 + PLU(3-7) +
// gramos(8-12) + DV. Ej.: 2 0 12345 01420 D → PLU 12345, 1.420 kg.
export interface BalanzaEtiquetaCfg {
  activo: boolean; prefijos: string;
  plu_desde: number; plu_longitud: number;
  valor_desde: number; valor_longitud: number;
  valor_tipo: 'peso_gramos' | 'precio'; validar_dv: boolean;
}
export const BALANZA_ETIQUETA_DEFAULT: BalanzaEtiquetaCfg = {
  activo: true, prefijos: '20,21', plu_desde: 3, plu_longitud: 5,
  valor_desde: 8, valor_longitud: 5, valor_tipo: 'peso_gramos', validar_dv: true,
};
export const balanzaEtiquetaGet = (): Promise<BalanzaEtiquetaCfg> => posRpc('balanza_etiqueta_get');
export const balanzaEtiquetaSet = (cfg: BalanzaEtiquetaCfg): Promise<BalanzaEtiquetaCfg> =>
  posRpc('balanza_etiqueta_set', { _cfg: cfg });

export interface PesoVariable { plu: string; pesoKg: number | null; importe: number | null }
export function parsearPesoVariable(codigo: string, cfg: BalanzaEtiquetaCfg = BALANZA_ETIQUETA_DEFAULT): PesoVariable | null {
  if (!cfg.activo) return null;
  const c = codigo.trim();
  if (!/^\d{13}$/.test(c)) return null;
  const prefijos = cfg.prefijos.split(',').map((p) => p.trim()).filter(Boolean);
  if (!prefijos.some((p) => c.startsWith(p))) return null;
  if (cfg.validar_dv) {
    let s = 0;
    for (let i = 0; i < 12; i++) s += Number(c[i]) * (i % 2 === 0 ? 1 : 3);
    if ((10 - (s % 10)) % 10 !== Number(c[12])) return null;
  }
  const plu = c.substring(cfg.plu_desde - 1, cfg.plu_desde - 1 + cfg.plu_longitud);
  const valor = Number(c.substring(cfg.valor_desde - 1, cfg.valor_desde - 1 + cfg.valor_longitud));
  if (!valor || !Number.isFinite(valor)) return null;
  return cfg.valor_tipo === 'precio'
    ? { plu, pesoKg: null, importe: valor / 100 }   // centavos → dólares
    : { plu, pesoKg: valor / 1000, importe: null }; // gramos → kg
}
// El PLU de la balanza suele ir con ceros a la izquierda: se prueba sin ceros
// (código interno típico) y tal cual.
export const candidatosPlu = (plu: string): string[] =>
  [...new Set([plu.replace(/^0+/, '') || '0', plu])];

// Estado puntual de una transacción (702/704) — para reimprimir el recibo de un
// servicio básico trae también celular (cuenta), tipo y el TIQUETE oficial.
export type BnTxEstadoResp = BnPagoServicioResp & { celular: string; tipo: string };
export const bnTxEstado = (txId: string): Promise<BnTxEstadoResp> =>
  posRpc<BnTxEstadoResp>('bn_tx_estado', { _tx_id: txId });
// Anti-doble-pago (701): último pago aprobado/confirmado de HOY para el mismo
// servicio y la misma cuenta — null si no hay.
export interface BnPagoPrevio { fecha: string; monto: number; referencia: string; autorizacion: string | null; caja: string | null }
export const bnPagoDuplicado = (productoId: string, referencia: string): Promise<BnPagoPrevio | null> =>
  posRpc<BnPagoPrevio | null>('bn_pago_duplicado', { _producto_id: productoId, _referencia: referencia });

// --- Broadnet Fase 2: configuración avanzada, catálogo, terminales y reglas ---
// (script 652: bn_config_get/set, bn_catalogo_seed, bn_codigos_seed, bn_productos_*,
// bn_codigos_*, bn_terminales_listar; bn_terminal_set/bn_transacciones/bn_regularizar
// son del 283 y ya están arriba)
export interface BnConfigFull {
  modo: string; host: string | null; esquema_comision: string | null;
  url_webservice: string | null; webservice_usuario: string | null;
  ftp_host: string | null; ftp_usuario: string | null; ftp_ruta: string | null;
  timeout_general_seg: number; timeout_recarga_seg: number; timeout_sb_seg: number;
  comision_default_pct: number; reintentos_recarga: number; reintentos_sb: number;
  es_activo: boolean;
  webservice_clave_configurada: boolean; ftp_clave_configurada: boolean;
  // Fase 5: quién factura la recarga ante el SRI.
  recarga_facturacion: 'comercio' | 'broadnet';
  recarga_iva_tarifa: number;
  recarga_producto_id: string | null;
  // Comisión de GESTIÓN (832): la que el comercio cobra AL CLIENTE, adicional
  // a la que Broadnet le paga a él.
  gestion_modo?: 'ninguna' | 'fija' | 'rangos';
  gestion_valor?: number;
  gestion_iva_pct?: number;
  gestion_captura?: 'total' | 'recarga';
}
export interface BnTramo { id?: string; desde: number; hasta: number | null; valor: number }
export const bnGestionTramos = async (productoId?: string | null): Promise<BnTramo[]> =>
  (await apiClient.post('/rpc/bn_gestion_tramos', { _producto_id: productoId ?? null }, POS)).data || [];
export const bnGestionTramosSet = async (productoId: string | null, tramos: BnTramo[]) =>
  (await apiClient.post('/rpc/bn_gestion_tramos_set',
    { _producto_id: productoId, _tramos: tramos }, POS)).data;
export const bnConfigGet = (): Promise<BnConfigFull> => posRpc('bn_config_get');
export const bnConfigSet = (cfg: Record<string, unknown>): Promise<BnConfigFull> => posRpc('bn_config_set', { _cfg: cfg });
// Opción B: asegura/devuelve el producto de inventario ('REC-<cod>') de esa operadora
// para facturar la recarga con desglose por operadora en los reportes de ventas.
export const bnRecargaProductoAsegurar = (broadnetProductoId: string): Promise<string> =>
  posRpc('bn_recarga_producto_asegurar', { _bp_id: broadnetProductoId });

export type TipoCampoBn = 'telefono' | 'texto' | 'numero' | 'cedula' | 'combo';
// `destino` (703): tag sb* del WS al que viaja el valor (p.ej. 'sbReferenciaAdicional'
// = regional CNEL). Sin destino, el valor forma parte de la CUENTA (campo <cuenta>).
export interface CampoRequeridoBn {
  clave: string; label: string; tipo: TipoCampoBn; requerido: boolean; destino?: string;
  opciones?: { valor: string; label: string }[];  // tipo 'combo' (p.ej. IESS: tipo de pago / concepto)
}
export const TIPOS_CAMPO_BN: { v: TipoCampoBn; label: string }[] = [
  { v: 'telefono', label: 'Teléfono' }, { v: 'texto', label: 'Texto' },
  { v: 'numero', label: 'Número' }, { v: 'cedula', label: 'Cédula' },
  { v: 'combo', label: 'Lista de opciones' },
];
// Tags sb* del WS Broadnet a los que puede dirigirse un campo (703); sin destino → va a la CUENTA
export const DESTINOS_CAMPO_BN = ['sbCedula', 'sbDireccion', 'sbTelefono', 'sbReferencia', 'sbReferenciaAdicional', 'sbCiudadCliente', 'sbContrapartidaNombre'];
export interface BnProductoFull {
  id: string; proveedor_codigo: string; proveedor_nombre: string;
  servicio_codigo: string; servicio_nombre: string; tipo: 'recarga' | 'servicio_basico';
  tran_codigo: string | null; soporta_reversa: boolean; soporta_consulta: boolean;
  monto_modo: 'fijo' | 'abierto' | 'rango'; monto_fijo: number | null; monto_min: number | null; monto_max: number | null;
  comision_pct: number | null; comision_valor: number | null;
  campos_requeridos: CampoRequeridoBn[]; orden: number; es_activo: boolean;
  // Quién factura ESTE producto. `facturacion` es lo elegido (puede ser
  // «heredar»); `facturacion_efectiva` es lo que va a pasar de verdad al
  // vender, con la herencia ya resuelta por la base.
  facturacion: 'heredar' | 'comercio' | 'broadnet';
  // Comisión de gestión de ESTE producto. «heredar» = la de la empresa (832).
  gestion_modo?: 'heredar' | 'ninguna' | 'fija' | 'rangos';
  gestion_valor?: number | null;
  gestion_iva_pct?: number | null;
  facturacion_efectiva: 'comercio' | 'broadnet';
  // 911: catálogo visual — marca (logo/color) y montos/paquetes del producto
  marca_id?: string | null;
  marca?: BnMarca | null;
  permite_otro_monto?: boolean;   // 913: false = sin la tarjeta «Otro monto» en el F4
  denominaciones?: BnDenominacion[];
}
export interface BnMarca { id: string; nombre: string; color: string; logo: string | null; orden: number; es_activo?: boolean; productos?: number }
export interface BnDenominacion { id: string; monto: number; etiqueta: string | null; imagen: string | null; orden: number; es_activo: boolean }
export const bnMarcasListar = (): Promise<BnMarca[]> => posRpc('bn_marcas_listar');
export const bnMarcaGuardar = (m: Record<string, unknown>): Promise<{ ok: boolean; id: string }> => posRpc('bn_marca_guardar', { _m: m });
export const bnMarcaEliminar = (id: string): Promise<{ ok: boolean; productos_sin_marca: number }> => posRpc('bn_marca_eliminar', { _id: id });
export const bnProductoMarca = (producto: string, marca: string | null): Promise<{ ok: boolean }> => posRpc('bn_producto_marca', { _producto: producto, _marca: marca });
export const bnDenominacionGuardar = (d: Record<string, unknown>): Promise<{ ok: boolean; id: string }> => posRpc('bn_denominacion_guardar', { _d: d });
export const bnDenominacionEliminar = (id: string): Promise<{ ok: boolean }> => posRpc('bn_denominacion_eliminar', { _id: id });
export const bnProductoOtroMonto = (producto: string, permite: boolean): Promise<{ ok: boolean }> => posRpc('bn_producto_otro_monto', { _producto: producto, _permite: permite });
// 914: cuánto pasar a bn_vender para que la recarga sea EXACTAMENTE `recarga` (la comisión va encima)
export const bnTecleadoParaRecarga = (producto: string, recarga: number): Promise<{ tecleado: number; recarga: number; total: number; comision: number }> => posRpc('bn_tecleado_para_recarga', { _bp_id: producto, _recarga: recarga });
export const bnCatalogoVisualSeed = (): Promise<{ marcas_creadas: number; productos_asignados: number; montos_creados: number }> => posRpc('bn_catalogo_visual_seed');
export const bnProductosListar = (tipo?: string | null): Promise<BnProductoFull[]> => posRpc('bn_productos_listar', { _tipo: tipo || null });
export const bnProductoGuardar = (p: Record<string, unknown>): Promise<{ ok: boolean; id: string }> => posRpc('bn_producto_guardar', { _p: p });
export const bnProductoEliminar = (id: string): Promise<{ ok: boolean }> => posRpc('bn_producto_eliminar', { _id: id });
export interface BnMargenFila {
  proveedor_nombre: string; servicio_nombre: string; tipo: string;
  facturacion: 'comercio' | 'broadnet'; n: number;
  vendido: number; costo: number; comision: number; margen: number; margen_pct: number;
}
export interface BnMargen {
  transacciones: number; vendido: number; costo: number; margen: number;
  con_perdida: number; detalle: BnMargenFila[];
}
export const bnMargen = (desde?: string | null, hasta?: string | null): Promise<BnMargen> =>
  posRpc('bn_margen', { _desde: desde || null, _hasta: hasta || null });

export const bnCatalogoSeed = (): Promise<{ recargas: number; servicios_basicos: number; total: number }> => posRpc('bn_catalogo_seed');

export interface BnCodigoRespuesta { id: string; codigo: string; accion: 'ok' | 'reverso' | 'reintento' | 'confirmar' | 'error'; descripcion: string | null; es_activo: boolean }
export const ACCIONES_CODIGO_BN: { v: BnCodigoRespuesta['accion']; label: string }[] = [
  { v: 'ok', label: 'OK (aprobada)' }, { v: 'reverso', label: 'Reverso' },
  { v: 'reintento', label: 'Reintento' }, { v: 'confirmar', label: 'Confirmar' }, { v: 'error', label: 'Error' },
];
export const bnCodigosListar = (): Promise<BnCodigoRespuesta[]> => posRpc('bn_codigos_listar');
export const bnCodigoGuardar = (c: Record<string, unknown>): Promise<{ ok: boolean; id: string }> => posRpc('bn_codigo_guardar', { _c: c });
export const bnCodigosSeed = (): Promise<{ insertados: number }> => posRpc('bn_codigos_seed');

export interface BnTerminal {
  id: string; caja_id: string; caja: string; tid: string | null; mid: string | null;
  cajero_bn: string | null; clave_configurada: boolean; es_activo: boolean; updated_at: string;
}
export const bnTerminalesListar = (): Promise<BnTerminal[]> => posRpc('bn_terminales_listar');
export const bnTerminalSet = (d: { caja_id: string; tid: string; mid: string; cajero: string; clave?: string | null }): Promise<unknown> =>
  posRpc('bn_terminal_set', { _caja_id: d.caja_id, _tid: d.tid, _mid: d.mid, _cajero: d.cajero, _clave: d.clave || null });

// --- Broadnet Fase 3: pago de servicios básicos (agua/luz/teléfono) ----------
export interface BnConsultaResp {
  ok: boolean; monto: number | null; titular: string | null; publicidad: string | null;
  tran_rec: string | null; mensaje: string | null; simulado?: boolean;
  proveedor: string | null; servicio: string | null; referencia: string;
}
// FASE 4 (script 696): en modo producción bn_consultar ENCOLA la consulta real
// ({pendiente:true, consulta_id}) y el broadnet-worker la resuelve contra el WS;
// este helper sondea bn_consulta_estado cada 1.5 s (máx ~30 s) y devuelve la
// MISMA forma que el modo simulado, así las pantallas no cambian.
export const bnConsultar = async (cajaId: string, productoId: string, referencia: string, extras?: Record<string, string> | null): Promise<BnConsultaResp> => {
  const r = await posRpc<BnConsultaResp & { pendiente?: boolean; consulta_id?: string }>(
    'bn_consultar', { _caja_id: cajaId, _producto_id: productoId, _referencia: referencia, _extras: extras ?? null });
  if (!r.pendiente || !r.consulta_id) return r;
  for (let i = 0; i < 20; i++) {
    await new Promise((res) => setTimeout(res, 1500));
    const est = await posRpc<{ estado: string; ok: boolean; monto: number | null; deuda: number | null; comision: number | null; titular: string | null; tran_rec: string | null; publicidad: string | null; mensaje: string | null }>(
      'bn_consulta_estado', { _consulta_id: r.consulta_id });
    if (est.estado === 'ok') {
      return { ...r, ok: true, monto: est.monto, titular: est.titular, tran_rec: est.tran_rec, publicidad: est.publicidad, mensaje: est.mensaje };
    }
    if (est.estado === 'error') {
      throw new Error(est.mensaje || 'Broadnet rechazó la consulta de deuda');
    }
  }
  throw new Error('Broadnet no respondió la consulta (30 s) — intenta de nuevo');
};

export interface BnPagoServicioResp {
  id: string; estado: string; codigo_respuesta: string | null; mensaje: string | null;
  referencia: string; proveedor: string; servicio: string; monto: number; titular: string | null;
  autorizacion: string | null; nota_venta: string | null; lote: string | null;
  tiquete?: string | null;   // recibo OFICIAL del proveedor (703) — se imprime tal cual
}
export const bnPagarServicio = async (
  cajaId: string, productoId: string, referencia: string, monto: number, titular: string | null, tranRec: string | null,
  extras?: Record<string, string> | null,
): Promise<BnPagoServicioResp> => {
  const r = await posRpc<BnPagoServicioResp>(
    'bn_pagar_servicio', { _caja_id: cajaId, _producto_id: productoId, _referencia: referencia, _monto: monto, _titular: titular, _tran_rec: tranRec, _extras: extras ?? null });
  // En producción la tx nace 'pendiente' y la resuelve el worker en segundos:
  // sondear el desenlace (702, patrón bnConsultar) para que el POS imprima el
  // voucher al aprobar en vez de quedarse "pendiente de regularizar".
  if (r.estado !== 'pendiente') return r;
  for (let i = 0; i < 20; i++) {
    await new Promise((res) => setTimeout(res, 1500));
    const est = await posRpc<Omit<BnPagoServicioResp, 'titular' | 'servicio' | 'proveedor'> & { proveedor: string; servicio: string }>(
      'bn_tx_estado', { _tx_id: r.id });
    if (est.estado !== 'pendiente') return { ...r, ...est, titular: r.titular };
  }
  return r; // sigue pendiente tras 30 s: la UI muestra el aviso de regularizar
};

// Arma la "referencia" que se manda a bn_consultar/bn_pagar_servicio a partir de los
// valores capturados en campos_requeridos: 1 solo campo → ese valor; varios → unidos con '|'
// (mismo orden que campos_requeridos, para que el proveedor los pueda desglosar).
export const armarReferenciaBn = (campos: CampoRequeridoBn[], valores: Record<string, string>): string =>
  campos.filter((c) => !c.destino).map((c) => (valores[c.clave] ?? '').trim()).join('|');

// Campos con `destino` (tags sb* del WS, 703): { sbReferenciaAdicional: '101', … }
export const armarExtrasBn = (campos: CampoRequeridoBn[], valores: Record<string, string>): Record<string, string> | null => {
  const extras: Record<string, string> = {};
  for (const c of campos) {
    const v = (valores[c.clave] ?? '').trim();
    if (c.destino && v) extras[c.destino] = v;
  }
  return Object.keys(extras).length ? extras : null;
};

// --- Ayuda del módulo Broadnet: checklist en vivo + estado actual ---------
export interface BnAyudaEstado {
  modo: string | null; recarga_facturacion: string | null; recarga_iva_tarifa: number;
  checklist: {
    config: boolean; modo_listo: boolean; productos: boolean; terminal: boolean;
    codigos: boolean; efectivo: boolean; worker: boolean;
  };
  productos_activos: number; terminales: number; pendientes: number; worker_vivo: boolean;
  ventas_hoy: { n: number; total: number };
}
export const bnAyudaEstado = (): Promise<BnAyudaEstado> => posRpc<BnAyudaEstado>('bn_ayuda_estado', {});
